import {
  Accordion,
  AccordionButton,
  AccordionIcon,
  AccordionItem,
  AccordionPanel,
  Box,
  Button,
  Flex,
  Tag,
  Text,
} from '@chakra-ui/react';
import { useState } from 'react';
import {
  ExistingSupplierUser,
  LinkedUserEntity,
} from '@texas/api/endpoints/contactsApi';
import { useTranslation } from 'react-i18next';

const ENTITY_BATCH_SIZE = 20;

interface EntityGroupProps {
  title: string;
  entities: LinkedUserEntity[];
}

interface ExistingUserLinksProps {
  user: ExistingSupplierUser;
}

export function ExistingUserLinks({ user }: ExistingUserLinksProps) {
  const { t } = useTranslation();
  const groups = [
    { title: t('general.customers'), entities: user.customers },
    { title: t('general.suppliers'), entities: user.suppliers },
    { title: t('general.articles'), entities: user.articles },
  ].filter((group) => group.entities.length > 0);

  return (
    <Box>
      <Text fontWeight="bold" mb={2}>
        {t('contacts.existingUserLinks')}
      </Text>
      {groups.length === 0 ? (
        <Text fontSize="sm">{t('contacts.noExistingUserLinks')}</Text>
      ) : (
        <Accordion allowMultiple={true}>
          {groups.map((group) => (
            <EntityGroup key={group.title} {...group} />
          ))}
        </Accordion>
      )}
    </Box>
  );
}

function EntityGroup({ title, entities }: EntityGroupProps) {
  const { t } = useTranslation();
  const [visibleCount, setVisibleCount] = useState(ENTITY_BATCH_SIZE);

  return (
    <AccordionItem>
      <AccordionButton>
        <Text flex={1} textAlign="left">
          {title} ({entities.length})
        </Text>
        <AccordionIcon />
      </AccordionButton>
      <AccordionPanel maxH={200} overflowY="auto">
        <Flex direction="column" gap={2}>
          {entities.slice(0, visibleCount).map((entity) => (
            <Flex key={entity.id} align="center" gap={2}>
              <Text fontSize="sm">{entity.name}</Text>
              {entity.archived && <Tag size="sm">{t('general.archived')}</Tag>}
            </Flex>
          ))}
          {visibleCount < entities.length && (
            <Button
              size="sm"
              alignSelf="start"
              onClick={() =>
                setVisibleCount((count) => count + ENTITY_BATCH_SIZE)
              }
            >
              {t('general.showMore')}
            </Button>
          )}
        </Flex>
      </AccordionPanel>
    </AccordionItem>
  );
}
